<template>
    <div>
        <pc-button-icon
            v-if="displayLeft"
            ref="leftButton"
            icon="copy"
            outline
            class="text-primary btn-outline-* pc-copyable"
            style="min-width: unset"
            v-bind:data-test-role="copyButtonDataTestRole"
            v-on:click.stop="copy" />
        <span v-if="!$slots.default && !showIconOnly">{{ copyString }}</span>
        <slot v-if="!showIconOnly" />
        <pc-button-icon
            v-if="!displayLeft"
            ref="rightButton"
            icon="copy"
            outline
            class="text-primary btn-outline-* pc-copyable"
            style="min-width: unset"
            v-bind:data-test-role="copyButtonDataTestRole"
            v-on:click.stop="copy" />
        <pc-popper
            ref="popper"
            v-bind:placement="popperPlacementComputed"
            v-bind:data-test-role="copiedPopperDataTestRole">
            <pc-bs-icon
                name="check-circle-fill"
                fill="var(--pc-drag-accept)"
                class="mb-1" />
            {{ pcCopyableFeedbackPopper }}
        </pc-popper>
    </div>
</template>

<script setup>
    import { ref, useSlots, computed } from "vue";
    import { KEYS, NAMESPACE } from "./i18n/pcComponentDefaults.i18n";
    import { useResource } from "Util/i18n";
    import { POPPER_PLACEMENT } from "Components/page/Navigation/popperComposable";

    // Refs
    const leftButton = ref(null);
    const rightButton = ref(null);
    const popper = ref(null);

    const props = defineProps({
        // Prop for when you want to display something different from what's being copied
        // Ex.) An embedded link to a text: the text is displayed, copyString is the link to be copied
        copyString: { type: String, required: false, default: null },
        displayLeft: { type: Boolean, required: false, default: false },
        showIconOnly: { type: Boolean, required: false, default: false },
        popperPlacement: { type: String, required: false, default: null },
        copyableId: { type: String, required: false, default: null }
    });

    const popperPlacementComputed = computed(() => {
        if (props.popperPlacement) {
            return props.popperPlacement;
        }

        // If button appears on the left, popper comes from the top.
        // If button appears on the right, popper comes from the right
        return props.displayLeft ? POPPER_PLACEMENT.top : POPPER_PLACEMENT.right;
    });

    // Computed data test roles using copyable-id to help UI tests know
    // exactly which element to test
    const copyButtonDataTestRole = computed(() => {
        if (props.copyableId) {
            return "CopyButton-" + props.copyableId;
        }
        return "CopyButton";
    });

    const copiedPopperDataTestRole = computed(() => {
        if (props.copyableId) {
            return "CopiedPopper-" + props.copyableId;
        }
        return "CopiedPopper";
    });

    // Determines which button the popper appears on
    const popper_referenceObject = props.displayLeft ? leftButton : rightButton;

    // Default displays slot. If no slot is given, displays copyString
    const slots = useSlots();

    function copy() {
        // If a copyString is given, then copy that to clipboard. Else, copy
        // inner text of the slot
        if (props.copyString) {
            navigator.clipboard.writeText(props.copyString.trim());
        } else {
            navigator.clipboard.writeText(slots.default()[0].children.trim());
        }

        popper.value.tryShow(popper_referenceObject.value.$el, 1500);
    }

    // i18n
    const { pcCopyableFeedbackPopper } = useResource(KEYS, NAMESPACE);
</script>

<style lang="scss" scoped>
    .pc-copyable:hover {
        background-color: var(--bs-gray-100);
    }
</style>
